import {
  Goal,
  goalInboxID,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
} from '@griply/types';

import { getSubGoals } from './goals/get-sub-goals';
import { sortGoals } from './sort-goals';
import { sortItems } from './sort-items';

export type BuildGoalSectionsOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  goalSortingMode?: GoalSortingMode;
  goalRoadmapSorting?: { inProgress?: ID[] | null } | null;
  goalGroupBy?: GroupByMode;
  goalGroupSorting?: {
    lifeArea?: { sorting?: Record<string, ID[]> | null } | null;
  } | null;
  lifeAreaSorting?: ID[];
  taskCounts?: Map<ID, number>;
};

export const buildGoalSections = (
  options: BuildGoalSectionsOptions,
): { id: string; items: [] }[] => {
  const {
    goals,
    lifeAreas,
    goalSortingMode,
    goalRoadmapSorting,
    goalGroupBy,
    goalGroupSorting,
    lifeAreaSorting,
    taskCounts,
  } = options;

  const mainGoals = goals.filter(({ parentIds }) => !parentIds?.length);
  const subGoals = goals.filter(({ parentIds }) => !!parentIds?.length);

  // let: reassigned when goalGroupBy === LifeArea
  let sortedMainGoals = sortGoals(mainGoals, {
    allGoals: goals,
    sortMode: goalSortingMode,
    customOrder: goalRoadmapSorting?.inProgress,
    taskCounts,
  });

  if (goalGroupBy === GroupByMode.LifeArea) {
    const sortedLifeAreas = sortItems(lifeAreas, lifeAreaSorting ?? [], 'id');
    sortedMainGoals = [
      ...sortedLifeAreas
        .map(({ id }) =>
          sortGoals(
            mainGoals.filter(({ lifeAreaId }) => lifeAreaId === id),
            {
              allGoals: goals,
              sortMode: goalSortingMode,
              customOrder: goalGroupSorting?.lifeArea?.sorting?.[id] ?? [],
              taskCounts,
            },
          ),
        )
        .flat(1),
      ...sortGoals(
        mainGoals.filter(({ lifeAreaId }) => !lifeAreaId),
        {
          allGoals: goals,
          sortMode: goalSortingMode,
          customOrder: goalGroupSorting?.lifeArea?.sorting?.null ?? [],
          taskCounts,
        },
      ),
    ];
  }

  const addSortedSubGoals = (parentGoal: Goal, allSubGoals: Goal[]): Goal[] => {
    const directSubGoals = getSubGoals(parentGoal.id, {
      goals: allSubGoals,
    });

    if (!directSubGoals.length) {
      return [];
    }

    const sortedDirectSubGoals = sortGoals(directSubGoals, {
      allGoals: goals,
      sortMode: parentGoal.goalSortingMode,
      customOrder: parentGoal.goalSorting,
      taskCounts,
    });

    const result: Goal[] = [];

    for (const subGoal of sortedDirectSubGoals) {
      result.push(subGoal);
      result.push(...addSortedSubGoals(subGoal, allSubGoals));
    }

    return result;
  };

  const orderedGoals = sortedMainGoals.reduce<Goal[]>((acc, goal) => {
    acc.push(goal);
    acc.push(...addSortedSubGoals(goal, subGoals));
    return acc;
  }, []);

  const goalOrder = orderedGoals.map((goal) => goal.id);

  return [
    ...sortItems(goals, goalOrder, 'id').map((goal) => ({
      id: goal.id,
      items: [] as [],
    })),
    {
      id: goalInboxID,
      items: [] as [],
    },
  ];
};
